/**
 * Suggested asks — the chips that give a blank composer somewhere to start.
 *
 * Shared by every Benny entry point (the web Today ask bar, the Conversations
 * welcome, the mobile home surface) so the same suggestion is never phrased
 * two ways. Each surface still decides how many chips it shows; only the
 * WORDING, the truncation and the slot order live here.
 */

/**
 * Fallbacks when the day is quiet. `liveAsks` below swaps in suggestions
 * built from what is actually waiting — a chip should read like a real ask,
 * not a category label.
 */
export const FALLBACK_ASKS = [
  'What needs me today?',
  'Show my overdue tasks',
  // Both answerable by a tool in one turn. The previous two ("Draft an
  // invoice for last month's work", "Which clients haven't heard from us
  // in 30 days?") turned into a hand-typed invoice form and a refusal (live
  // run 07 §A). The trailing "…" is the prefill convention: the user names
  // the client before sending.
  'Show my open tickets',
  'Catch me up on a client…',
] as const

/**
 * A chip is a one-line ask, so its label is capped — but on a WORD boundary.
 * Slicing mid-word ("Staging environment login brok…") reads like a bug; the
 * cut lands on the last space before the cap instead.
 *
 * 44 rather than a flat 40 so the approvals ask ("Review the 128 entries
 * pending my approval") survives whole at any realistic count — a truncated
 * verb phrase loses the meaning the chip exists to carry.
 */
export const MAX_CHIP_LABEL = 44

export function truncateChipLabel(label: string, max = MAX_CHIP_LABEL): string {
  if (label.length <= max) return label
  const clipped = label.slice(0, max)
  const lastSpace = clipped.lastIndexOf(' ')
  // Only honor the boundary when it leaves a readable stub; a single very
  // long word falls back to a hard cut rather than to nothing.
  const cut = lastSpace > max / 2 ? clipped.slice(0, lastSpace) : clipped
  return `${cut.replace(/[\s,;:.\-–—]+$/, '')}…`
}

/** What the day itself has to offer. Keys are absent when there's nothing real. */
export interface LiveAsks {
  approvals?: string
  topTicket?: string
}

/**
 * The asks that come from the day itself. A key is left off where there's
 * nothing genuine to ask about — callers fall back rather than inventing a
 * number.
 */
export function liveAsks(input: {
  pendingApprovals: number
  topTicketTitle?: string | null
}): LiveAsks {
  const asks: LiveAsks = {}
  if (input.pendingApprovals > 0) {
    // Phrased as the approver's ask — "pending my approval" steers Benny to
    // the approvals queue, not the user's own My Time list.
    asks.approvals = truncateChipLabel(
      `Review the ${input.pendingApprovals} ${
        input.pendingApprovals === 1 ? 'entry' : 'entries'
      } pending my approval`,
    )
  }
  if (input.topTicketTitle) {
    asks.topTicket = truncateChipLabel(`Catch me up on ${input.topTicketTitle}`)
  }
  return asks
}

/**
 * Fill a surface's ask slots: the ask that always applies, then approvals,
 * then the top ticket — each falling back to a generic ask when there is
 * nothing real to put there — then the remaining fallbacks up to `count`.
 * Fixed slots mean the bar never reflows as the day changes.
 */
export function buildSuggestedAsks(live: LiveAsks, count = 3): string[] {
  const asks: string[] = []
  const push = (ask: string | undefined) => {
    if (!ask || asks.length >= count || asks.includes(ask)) return
    asks.push(ask)
  }

  push(FALLBACK_ASKS[0])
  push(live.approvals ?? FALLBACK_ASKS[1])
  push(live.topTicket ?? FALLBACK_ASKS[2])
  for (const fallback of FALLBACK_ASKS) push(fallback)

  return asks
}

/**
 * Context chips that say the same thing as a fallback. The welcome dedupes
 * chips against fallbacks by exact string, so the server's core chip "What
 * should I work on first?" used to sit next to "What needs me today?" and
 * push "Catch me up on a client…" off a busy day (07e finding 2). The
 * fallback wins: it is always offered, so the chip adds nothing.
 */
export const WELCOME_OVERLAPS: Record<string, string> = {
  'What should I work on first?': 'What needs me today?',
}

/** True when a context chip repeats a fallback ask in other words. */
export function overlapsFallback(prompt: string): boolean {
  const partner = WELCOME_OVERLAPS[prompt]
  return (
    partner !== undefined &&
    (FALLBACK_ASKS as readonly string[]).includes(partner)
  )
}

/**
 * The Conversations welcome's slots: live asks first (what is genuinely
 * waiting), then chips from the page or anchored record, then the four
 * fallbacks. A quiet day is five slots with two context chips. A busy day
 * (any live ask) is six slots and every fallback lands, the client
 * catch-up stem included; context chips take only the room left over (one
 * with one live ask, none with two). One live ask plus two context chips
 * plus four fallbacks is seven asks, and no five-slot rule can seat the
 * stem (Pass 5, 07f finding 1). A context chip that only rewords a
 * fallback is skipped (see `WELCOME_OVERLAPS`).
 */
export function buildWelcomeAsks(
  live: string[],
  context: string[],
  max = live.length > 0 ? 6 : 5,
  maxContext = live.length > 0
    ? Math.max(0, max - live.length - FALLBACK_ASKS.length)
    : 2,
): string[] {
  const asks: string[] = []
  const push = (ask: string) => {
    if (asks.length >= max || asks.includes(ask)) return
    asks.push(ask)
  }
  for (const ask of live) push(ask)
  let contextUsed = 0
  for (const chip of context) {
    if (contextUsed >= maxContext) break
    if (overlapsFallback(chip) || asks.includes(chip)) continue
    push(chip)
    contextUsed += 1
  }
  for (const fallback of FALLBACK_ASKS) push(fallback)
  return asks
}

/**
 * A chip label that ends with "…" (or "...") is a stem the user finishes,
 * not a sendable ask. Returns the stem with a trailing space, or null when
 * the label is complete and should be sent as is. Every Benny composer
 * honors it (welcome, Today bar, docked chat), so it lives here.
 */
export function askStem(text: string): string | null {
  const stemMatch = /(\.{3}|…)\s*$/.exec(text)
  if (!stemMatch) return null
  return `${text.slice(0, stemMatch.index).trimEnd()} `
}
